/**
 * Weather Service for TeeSync - WeatherKit Only
 *
 * Uses WeatherKit as the sole weather provider:
 * - 0-10 days: WeatherKit hourly data (240 hours) + daily (10 days)
 *
 * Focuses on 5 core metrics: Temperature, Wind Speed, Precipitation, Humidity, Daylight
 */

import { calculateDaylightScore, getSunTimes } from './sunService.js';
import { supabase } from '../lib/supabase';
import {
  WEATHER_CURVES,
  WEATHER_WEIGHTS,
  DAILY_TO_ROUND_RATIO,
  GROUND_CONFIG,
  GROUND_DRAG_WEIGHT,
  FROZEN_GROUND_SAFETY_SCORE,
  INTENSITY_CONFIG,
  ET0_ESTIMATE,
  UNPLAYABLE_THRESHOLDS,
  categoriseScore
} from './scoringConfig.js';

// WeatherKit coverage (in days from now)
const TIER1_MAX_DAYS = 10;  // 0-10 days: WeatherKit hourly data (240 hours)

/**
 * Helper function to format dates to YYYY-MM-DD in local timezone
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Date in YYYY-MM-DD format
 */
function formatDateLocal(date) {
  try {
    // If it's already a date string in YYYY-MM-DD format, just return the date part
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}/)) {
      return date.split('T')[0];
    }

    const dateObj = date instanceof Date ? date : new Date(date);
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.log('Invalid date object:', date);
      return null;
    }
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.log('Error in formatDateLocal:', error, 'for input:', date);
    return null;
  }
}

/**
 * Calculate days from now for a given date
 * @param {Date} date - Date to check
 * @returns {number} - Number of days from now (0 = today)
 */
function getDaysFromNow(date) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = startOfDate - startOfToday;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

// UNPLAYABLE_THRESHOLDS lives in scoringConfig.js
// (SIMPLE_GOLF_THRESHOLDS was the v1 discrete-bucket model - removed; replaced by smooth curves in calculateSimpleGolfScore.)

/**
 * Calculate precipitation during round from hourly data
 * @param {Array} hourlyData - Array of hourly forecast objects
 * @param {Date} roundStart - Round start time
 * @param {number} durationHours - Round duration in hours
 * @returns {Object} - { total: mm, source: 'hourly' }
 */
function calculateRoundPrecipFromHourly(hourlyData, roundStart, durationHours = 4) {
  const roundEnd = new Date(roundStart.getTime() + (durationHours * 60 * 60 * 1000));
  const roundStartTs = Math.floor(roundStart.getTime() / 1000);
  const roundEndTs = Math.floor(roundEnd.getTime() / 1000);

  let totalPrecip = 0;

  for (const hour of hourlyData) {
    // Each hourly entry represents the hour starting at dt
    const hourStart = hour.dt;
    const hourEnd = hourStart + 3600; // 1 hour later

    // Check if this hour overlaps with the round
    if (hourEnd > roundStartTs && hourStart < roundEndTs) {
      // Calculate overlap fraction
      const overlapStart = Math.max(hourStart, roundStartTs);
      const overlapEnd = Math.min(hourEnd, roundEndTs);
      const overlapFraction = (overlapEnd - overlapStart) / 3600;

      // Add proportional precipitation
      const hourlyRain = hour.rain?.['1h'] || 0;
      totalPrecip += hourlyRain * overlapFraction;
    }
  }

  return {
    total: Math.round(totalPrecip * 10) / 10, // Round to 1 decimal
    source: 'hourly'
  };
}

/**
 * Get temperature from hourly data for round midpoint
 * @param {Array} hourlyData - Array of hourly forecast objects
 * @param {Date} roundStart - Round start time
 * @param {number} durationHours - Round duration in hours
 * @returns {number|null} - Temperature in Celsius
 */
function getTemperatureFromHourly(hourlyData, roundStart, durationHours = 4) {
  const midpoint = new Date(roundStart.getTime() + (durationHours / 2) * 60 * 60 * 1000);
  const midpointTs = Math.floor(midpoint.getTime() / 1000);

  // Find the closest hourly reading
  let closest = null;
  let closestDiff = Infinity;

  for (const hour of hourlyData) {
    const diff = Math.abs(hour.dt - midpointTs);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = hour;
    }
  }

  return closest?.temp ?? null;
}

/**
 * Get humidity from hourly data for round midpoint
 */
function getHumidityFromHourly(hourlyData, roundStart, durationHours = 4) {
  const midpoint = new Date(roundStart.getTime() + (durationHours / 2) * 60 * 60 * 1000);
  const midpointTs = Math.floor(midpoint.getTime() / 1000);

  let closest = null;
  let closestDiff = Infinity;

  for (const hour of hourlyData) {
    const diff = Math.abs(hour.dt - midpointTs);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = hour;
    }
  }

  return closest?.humidity ?? 50;
}

/**
 * Get wind speed from hourly data for round (max during round)
 */
function getWindSpeedFromHourly(hourlyData, roundStart, durationHours = 4) {
  const roundEnd = new Date(roundStart.getTime() + (durationHours * 60 * 60 * 1000));
  const roundStartTs = Math.floor(roundStart.getTime() / 1000);
  const roundEndTs = Math.floor(roundEnd.getTime() / 1000);

  let maxWind = 0;

  for (const hour of hourlyData) {
    if (hour.dt >= roundStartTs && hour.dt < roundEndTs) {
      const windSpeed = hour.wind_speed || 0;
      if (windSpeed > maxWind) {
        maxWind = windSpeed;
      }
    }
  }

  // Convert m/s to mph
  return maxWind * 2.237;
}

/**
 * Get max UV index from hourly data during round window
 */
function getUVFromHourly(hourlyData, roundStart, durationHours = 4) {
  const roundEnd = new Date(roundStart.getTime() + (durationHours * 60 * 60 * 1000));
  const roundStartTs = Math.floor(roundStart.getTime() / 1000);
  const roundEndTs = Math.floor(roundEnd.getTime() / 1000);

  let maxUV = 0;

  for (const hour of hourlyData) {
    if (hour.dt >= roundStartTs && hour.dt < roundEndTs) {
      maxUV = Math.max(maxUV, hour.uvIndex || 0);
    }
  }

  return maxUV;
}

/**
 * Calculate golf suitability score using smooth mathematical curves (0-10 scale)
 * Scoring System v2.0 - Uses continuous functions for nuanced rankings
 *
 * Weather factors use smooth curves instead of discrete thresholds:
 * - Temperature: Gaussian curve centred at 21°C
 * - Wind: Logistic decay curve (midpoint 15mph)
 * - Precipitation: Reciprocal decay (hourly) or soft penalty (daily)
 * - Humidity: Parabolic curve centred at 50%
 *
 * @param {Object} weatherData - Weather data object
 * @param {string} units - Unit system ('imperial' or 'metric')
 * @param {Object} daylightInfo - Optional daylight info { dateStr, startTime, durationHours, lat, lon }
 * @param {string} dataSource - Data source ('hourly', 'weatherkit-daily') for weight selection
 */
function calculateSimpleGolfScore(weatherData, units = 'imperial', daylightInfo = null, dataSource = null) {
  // Extract values (temperature is already in Celsius from API)
  let temp = weatherData.temperature || weatherData.temp;
  let windSpeed = weatherData.windSpeed || weatherData.wind_speed || 0;
  let precipitation = weatherData.precipitation || 0;
  let humidity = weatherData.humidity || 50;

  // Determine if this is "rain during round" or "rain on the day" based on actual data source
  const isRainDuringRound = dataSource === 'hourly';

  // === SMOOTH CURVE SCORING FUNCTIONS ===

  // Temperature - Gaussian
  let tempScore = 0;
  if (temp !== undefined && temp !== null) {
    tempScore = Math.exp(-Math.pow((temp - WEATHER_CURVES.temp.centre) / WEATHER_CURVES.temp.sigma, 2));
  }

  // Wind - Logistic decay
  const windScore = 1 / (1 + Math.exp((windSpeed - WEATHER_CURVES.wind.midpoint) / WEATHER_CURVES.wind.steepness));

  // Precipitation - reciprocal decay (normalise daily totals to round-equivalent first)
  const normalizedPrecip = isRainDuringRound
    ? precipitation
    : precipitation / DAILY_TO_ROUND_RATIO;
  let precipScore = 1 / (1 + Math.pow(normalizedPrecip / WEATHER_CURVES.precip.scale, WEATHER_CURVES.precip.exponent));

  // Humidity - one-sided sigmoid (only penalises high humidity)
  const humidityScore = 1 / (
    1 + Math.exp((humidity - WEATHER_CURVES.humidity.midpoint) / WEATHER_CURVES.humidity.steepness)
  );

  // Clamp all scores to [0, 1]
  tempScore = Math.max(0, Math.min(1, tempScore));
  const windScoreClamped = Math.max(0, Math.min(1, windScore));
  precipScore = Math.max(0, Math.min(1, precipScore));
  const humidityScoreClamped = Math.max(0, Math.min(1, humidityScore));

  // === SOURCE-AWARE WEIGHTS ===
  // Hourly: rain timing known → precip dominates. Daily: balanced.
  const weights = isRainDuringRound ? WEATHER_WEIGHTS.hourly : WEATHER_WEIGHTS.daily;

  // Calculate weighted weather score (0-1 scale, then multiply by 10)
  const weatherScore = (
    tempScore * weights.temp +
    windScoreClamped * weights.wind +
    precipScore * weights.precip +
    humidityScoreClamped * weights.humidity
  ) * 10;

  // === DAYLIGHT SCORING (if coordinates available) ===
  let daylightScore = null;
  let daylightCategory = 'unknown';
  let sunTimes = null;

  if (daylightInfo && daylightInfo.lat !== undefined && daylightInfo.lon !== undefined) {
    const daylightResult = calculateDaylightScore(
      daylightInfo.dateStr,
      daylightInfo.startTime,
      daylightInfo.durationHours,
      daylightInfo.lat,
      daylightInfo.lon
    );
    daylightScore = daylightResult.score;
    daylightCategory = daylightResult.category;

    // Get sun times for display
    sunTimes = getSunTimes(daylightInfo.dateStr, daylightInfo.lat, daylightInfo.lon);
  }

  // Final score is just the weather score (daylight handled separately in composite)
  const finalScore = Math.round(weatherScore * 10) / 10;
  const category = categoriseScore(finalScore);

  // Convert continuous scores to 0-10 for factor display (rounded to 1 decimal)
  const result = {
    score: finalScore,
    category,
    factors: [
      { factor: 'temperature', score: Math.round(tempScore * 100) / 10, value: temp ? `${Math.round(temp)}°C` : 'N/A' },
      { factor: 'windSpeed', score: Math.round(windScoreClamped * 100) / 10, value: `${Math.round(windSpeed)} mph` },
      { factor: 'precipitation', score: Math.round(precipScore * 100) / 10, value: `${Math.round(precipitation)} mm` },
      { factor: 'humidity', score: Math.round(humidityScoreClamped * 100) / 10, value: `${Math.round(humidity)}%` }
    ]
  };

  // Add daylight info if available
  if (daylightScore !== null) {
    result.factors.push({
      factor: 'daylight',
      score: daylightScore,
      category: daylightCategory
    });
    result.daylightCategory = daylightCategory;
  }

  // Add sun times if available
  if (sunTimes) {
    result.sunTimes = {
      sunrise: sunTimes.sunrise,
      sunset: sunTimes.sunset
    };
  }

  return result;
}

/**
 * Custom error class for rate limit exceeded
 */
class RateLimitError extends Error {
  constructor(message, resetAt, currentUsage, limits) {
    super(message);
    this.name = 'RateLimitError';
    this.resetAt = resetAt;
    this.currentUsage = currentUsage;
    this.limits = limits;
  }
}

/**
 * Format reset time for user-friendly display
 */
function formatResetTime(resetAt) {
  if (!resetAt) return 'soon';
  const resetDate = new Date(resetAt);
  const now = new Date();
  const diffMs = resetDate - now;
  const diffMins = Math.ceil(diffMs / 60000);

  if (diffMins <= 1) return 'in less than a minute';
  if (diffMins < 60) return `in ${diffMins} minute${diffMins > 1 ? 's' : ''}`;

  const diffHours = Math.ceil(diffMins / 60);
  return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
}

/**
 * Check if response contains rate limit error
 */
function checkRateLimitError(data) {
  if (data?.error === 'Rate limit exceeded') {
    throw new RateLimitError(
      `API rate limit exceeded. Resets ${formatResetTime(data.reset_at)}`,
      data.reset_at,
      data.current_usage,
      data.limits
    );
  }
}

/**
 * Fetch weather from WeatherKit edge function
 * Returns normalised data: { current, hourly[], daily[], minutely[], alerts[] }
 * Hourly covers 240 hours (10 days), daily covers 10 days
 *
 * Includes client-side request deduplication - concurrent calls for the
 * same location (~1km) share a single in-flight request.
 */
const _pendingWeatherKitRequests = new Map();

async function fetchWeatherKit(lat, lon, options = {}) {
  const { retries = 1, timezone = 'UTC', countryCode } = options;

  // Deduplicate concurrent requests for the same location (~1km precision)
  const dedupeKey = `${Math.round(lat * 100)},${Math.round(lon * 100)}`;
  if (_pendingWeatherKitRequests.has(dedupeKey)) {
    return _pendingWeatherKitRequests.get(dedupeKey);
  }

  const request = _fetchWeatherKitInner(lat, lon, retries, timezone, countryCode);
  _pendingWeatherKitRequests.set(dedupeKey, request);
  request.finally(() => _pendingWeatherKitRequests.delete(dedupeKey));
  return request;
}

async function _fetchWeatherKitInner(lat, lon, retries, timezone, countryCode) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const body = {
        lat,
        lon,
        timezone,
        countryCode,
      };

      const { data, error } = await supabase.functions.invoke('weatherkit', { body });

      // Check for rate limit error in response data
      if (data?.error === 'Rate limit exceeded') {
        checkRateLimitError(data);
      }

      if (error) {
        throw new Error(`WeatherKit API failed: ${error.message}`);
      }

      return data;

    } catch (error) {
      // Re-throw rate limit errors immediately (don't retry)
      if (error instanceof RateLimitError) {
        throw error;
      }

      console.warn(`WeatherKit API error (attempt ${attempt + 1}):`, error.message);
      if (attempt === retries) {
        return null; // Return null on failure, will fall back
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  return null;
}

/**
 * Fetch historical daily rainfall AND server-computed FAO-56 Penman-Monteith
 * reference evapotranspiration (ET0) from the weatherkit-history edge
 * function. WeatherKit's daily endpoint exposes humidity + cloudCover via
 * its daytime/overnight sub-objects, so PM is fully computable on the
 * server side and cached alongside the rainfall.
 *
 * Subtracting ET0 from each day's gross rain gives physical "net rain"
 * - more accurate than the previous drying-multiplier approximation.
 *
 * Returns { rainfall: { 'YYYY-MM-DD': mm }, et0: { ... } } or empty
 * objects on failure (ground quality degrades gracefully).
 */
const _pendingHistoricalRequests = new Map();

async function fetchHistoricalRainAndET(lat, lon, days = 7) {
  const dedupeKey = `${Math.round(lat * 100)},${Math.round(lon * 100)}:${days}`;
  if (_pendingHistoricalRequests.has(dedupeKey)) {
    return _pendingHistoricalRequests.get(dedupeKey);
  }

  const request = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('weatherkit-history', {
        body: { lat, lon, days },
      });
      if (error) {
        console.warn('[Weather] Historical rain+ET fetch failed:', error.message);
        return { rainfall: {}, et0: {} };
      }
      return { rainfall: data?.rainfall || {}, et0: data?.et0 || {} };
    } catch (err) {
      console.warn('[Weather] Historical rain+ET fetch error:', err.message);
      return { rainfall: {}, et0: {} };
    }
  })();

  _pendingHistoricalRequests.set(dedupeKey, request);
  request.finally(() => _pendingHistoricalRequests.delete(dedupeKey));
  return request;
}

/**
 * Get weather data for multiple time slots using tiered approach
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {Array} timeSlots - Array of time slot objects
 * @param {Object} options - Options including units, durationHours
 */
export async function getWeatherForTimeSlots(lat, lon, timeSlots, options = {}) {
  try {
    const { units = 'imperial', durationHours = 4 } = options;
    const maxForecastLimit = new Date(Date.now() + (548 * 24 * 60 * 60 * 1000)); // ~1.5 years ahead

    // Fetch WeatherKit (forecast: now + 240h hourly + 10d daily) and Open-Meteo
    // historical observations (past 7 days, rain + ET0) in parallel.
    let weatherKitData = null;
    let historical = { rainfall: {}, et0: {} };

    const hasSlotsInRange = timeSlots.some(slot => {
      const daysFromNow = getDaysFromNow(new Date(slot.startTime || slot.datetime));
      return daysFromNow <= TIER1_MAX_DAYS;
    });

    if (hasSlotsInRange) {
      [weatherKitData, historical] = await Promise.all([
        fetchWeatherKit(lat, lon, options),
        fetchHistoricalRainAndET(lat, lon, 7),
      ]);
    }

    // Log data availability for debugging
    console.log('[Weather] Data available:', {
      weatherKitHourly: weatherKitData?.hourly?.length || 0,
      weatherKitDaily: weatherKitData?.daily?.length || 0,
      historicalRainDays: Object.keys(historical.rainfall || {}).length,
      historicalET0Days: Object.keys(historical.et0 || {}).length,
    });

    // === BUILD RAINFALL BUFFER FOR GROUND QUALITY ===
    // Layering, lowest-precedence first:
    //   1. Open-Meteo observed (past dates only; WeatherKit has nothing for past)
    //   2. WeatherKit daily forecast (today + future, lower resolution)
    //   3. WeatherKit hourly forecast (today + 10 days, highest resolution - overrides)
    let rainfallBuffer = { ...(historical.rainfall || {}) };

    // Extract rainfall from WeatherKit daily data (10 days, forecast)
    if (weatherKitData?.daily) {
      const wkDailyRainfall = extractDailyRainfallFromDaily(weatherKitData.daily);
      for (const [date, rain] of Object.entries(wkDailyRainfall)) {
        if (rainfallBuffer[date] === undefined) {
          rainfallBuffer[date] = rain;
        }
      }
    }

    // Extract rainfall from WeatherKit hourly data (most precise, 240 hours).
    // Hourly always wins where present.
    if (weatherKitData?.hourly) {
      const hourlyRainfall = extractDailyRainfallFromHourly(weatherKitData.hourly);
      rainfallBuffer = { ...rainfallBuffer, ...hourlyRainfall };
    }

    // ET0 buffer is past-only (WeatherKit doesn't expose ET0). The round-day
    // ET0 is estimated from current weather conditions inside the ground
    // calculation since the round day isn't in the historical buffer yet.
    const et0Buffer = { ...(historical.et0 || {}) };

    console.log('[Weather] Rainfall buffer built:', Object.keys(rainfallBuffer).length, 'days');

    // Process each slot with the appropriate data source
    return timeSlots.map(slot => {
      const slotDate = new Date(slot.startTime || slot.datetime);
      const dateKey = formatDateLocal(slotDate);
      const daysFromNow = getDaysFromNow(slotDate);

      // Beyond 1.5 years - no data available
      if (slotDate > maxForecastLimit) {
        return {
          ...slot,
          weather: null,
          weatherScore: 0,
          weatherError: 'Weather forecast not available beyond 1.5 years',
          dataSource: 'unavailable',
          accuracy: 'none'
        };
      }

      let weatherData = null;
      let precipitationSource = 'daily';
      let precipitationLabel = 'Rain on the day';
      let dataSource = 'unavailable';

      // Try Tier 1: WeatherKit hourly data (0-10 days)
      if (daysFromNow <= TIER1_MAX_DAYS && weatherKitData?.hourly) {
        try {
          const precipResult = calculateRoundPrecipFromHourly(weatherKitData.hourly, slotDate, durationHours);
          const temperature = getTemperatureFromHourly(weatherKitData.hourly, slotDate, durationHours);
          const humidity = getHumidityFromHourly(weatherKitData.hourly, slotDate, durationHours);
          const windSpeed = getWindSpeedFromHourly(weatherKitData.hourly, slotDate, durationHours);
          const uvIndex = getUVFromHourly(weatherKitData.hourly, slotDate, durationHours);

          if (temperature !== null) {
            weatherData = {
              temperature,
              windSpeed,
              precipitation: precipResult.total,
              precipitationSource: 'hourly',
              precipitationLabel: 'Rain during round',
              humidity,
              uvIndex,
              units
            };
            precipitationSource = 'hourly';
            precipitationLabel = 'Rain during round';
            dataSource = 'hourly';
          }
        } catch (error) {
          console.warn('Error processing WeatherKit hourly data:', error);
        }
      }

      // Fallback: WeatherKit daily[] if hourly extraction failed
      if (!weatherData && daysFromNow <= TIER1_MAX_DAYS && weatherKitData?.daily) {
        try {
          // Find the daily entry matching this slot's date
          const slotDateStart = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());

          const dailyEntry = weatherKitData.daily.find(d => {
            const entryDate = new Date(d.dt * 1000);
            const entryDateStart = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
            return entryDateStart.getTime() === slotDateStart.getTime();
          });

          if (dailyEntry) {
            const temperature = dailyEntry.temp?.day || dailyEntry.temp?.afternoon;
            let windSpeed = dailyEntry.wind_speed || 0;
            windSpeed = windSpeed * 2.237; // m/s to mph
            const precipitation = dailyEntry.rain || 0;
            const humidity = dailyEntry.humidity || 50;

            weatherData = {
              temperature,
              windSpeed,
              precipitation,
              precipitationSource: 'daily',
              precipitationLabel: 'Rain on the day',
              humidity,
              units
            };
            precipitationSource = 'daily';
            precipitationLabel = 'Rain on the day';
            dataSource = 'weatherkit-daily';
          }
        } catch (error) {
          console.warn('Error processing WeatherKit daily data:', error);
        }
      }

      // If we still have no data, return error state
      if (!weatherData) {
        return {
          ...slot,
          weather: null,
          weatherScore: 0,
          weatherError: 'Weather data not available for this date',
          dataSource: 'unavailable',
          accuracy: 'none'
        };
      }

      // Add compatibility fields
      weatherData.temp = weatherData.temperature;
      weatherData.wind_speed = weatherData.windSpeed;
      weatherData.dt = Math.floor(slotDate.getTime() / 1000);
      weatherData.timestamp = slotDate.getTime();
      weatherData.datetime = slotDate.toISOString();

      // Extract start time for daylight calculation
      const startTimeStr = slot.startTime || slot.datetime;
      let startTimeHHMM = '10:00';
      if (startTimeStr) {
        const startDate = new Date(startTimeStr);
        const hours = String(startDate.getHours()).padStart(2, '0');
        const mins = String(startDate.getMinutes()).padStart(2, '0');
        startTimeHHMM = `${hours}:${mins}`;
      }

      // Build daylight info for scoring
      const daylightInfo = (lat !== undefined && lon !== undefined) ? {
        dateStr: dateKey,
        startTime: startTimeHHMM,
        durationHours: durationHours,
        lat: lat,
        lon: lon
      } : null;

      // Calculate golf score - pass dataSource for source-aware precipitation thresholds
      weatherData.golfScore = calculateSimpleGolfScore(weatherData, units, daylightInfo, dataSource);

      // === CALCULATE GROUND QUALITY ===
      // Build 7-day history arrays for this slot's date (rain + ET0).
      const rainfallHistory = getRainfallHistory(slotDate, rainfallBuffer);
      const et0History = getRainfallHistory(slotDate, et0Buffer); // same shape, swap buffer

      // Include slot's forecasted precipitation in the ground calculation
      // This ensures the ground quality reflects rain expected on that day
      // rainfallHistory[0] = slot day rainfall from buffer (may be partial/zero for future dates)
      // weatherData.precipitation = forecasted rain for this specific slot
      const slotDayRain = Math.max(rainfallHistory[0] || 0, weatherData.precipitation || 0);
      const adjustedRainfallHistory = [slotDayRain, ...rainfallHistory.slice(1)];

      const groundQuality = calculateGroundQualityScore(
        adjustedRainfallHistory,
        weatherData.temperature,
        weatherData.humidity,
        weatherData.windSpeed,
        et0History
      );

      // Attach ground quality to weather data
      weatherData.groundQuality = groundQuality;

      // === INTEGRATE GROUND QUALITY INTO WEATHER SCORE ===
      // Ground quality contributes 20% of the weather score (increased from 15%)
      // Original weather factors (temp, wind, precip, humidity) contribute 80%
      // Rationale: Ground conditions significantly impact playability
      if (weatherData.golfScore && groundQuality) {
        const originalScore = weatherData.golfScore.score;
        const groundScore = groundQuality.score;

        // Asymmetric integration: ground can drag the score down (waterlogged
        // courses penalised) but cannot lift it (firm ground does not rescue
        // bad weather). See GROUND_DRAG_WEIGHT docstring.
        const blended =
          (originalScore * (1 - GROUND_DRAG_WEIGHT)) +
          (groundScore * GROUND_DRAG_WEIGHT);
        const adjustedScore = Math.min(originalScore, blended);
        weatherData.golfScore.score = Math.round(adjustedScore * 10) / 10;
        weatherData.golfScore.category = categoriseScore(weatherData.golfScore.score);

        // Store original score for debugging
        weatherData.golfScore.originalScore = originalScore;

        // Add ground quality to factors array for UI display
        weatherData.golfScore.factors.push({
          factor: 'groundQuality',
          score: groundScore,
          value: `${groundQuality.effectiveRainfall}mm`,
          category: groundQuality.category
        });

        // Store ground quality details
        weatherData.golfScore.groundQuality = groundQuality;
      }

      // Check if conditions are unplayable based on actual data source
      // This handles API fallbacks correctly (e.g., hourly failing and falling back to daily)
      const isRainDuringRound = dataSource === 'hourly';
      const precipThreshold = isRainDuringRound
        ? UNPLAYABLE_THRESHOLDS.precipitationDuringRound
        : UNPLAYABLE_THRESHOLDS.precipitationOnDay;

      // Check if ground quality makes the course unplayable (waterlogged)
      const isWaterlogged = groundQuality?.score < UNPLAYABLE_THRESHOLDS.groundQuality;

      weatherData.isUnplayable = (
        weatherData.precipitation > precipThreshold ||
        weatherData.windSpeed > UNPLAYABLE_THRESHOLDS.windSpeed ||
        weatherData.temperature < UNPLAYABLE_THRESHOLDS.temperature.min ||
        weatherData.temperature > UNPLAYABLE_THRESHOLDS.temperature.max ||
        isWaterlogged
      );

      // Set reason for unplayable conditions (for logging/debugging)
      weatherData.unplayableReason = null;
      if (weatherData.isUnplayable) {
        if (weatherData.precipitation > precipThreshold) {
          weatherData.unplayableReason = `Heavy rain (${Math.round(weatherData.precipitation)}mm > ${precipThreshold}mm threshold)`;
        } else if (weatherData.windSpeed > UNPLAYABLE_THRESHOLDS.windSpeed) {
          weatherData.unplayableReason = `High winds (${Math.round(weatherData.windSpeed)}mph > ${UNPLAYABLE_THRESHOLDS.windSpeed}mph threshold)`;
        } else if (weatherData.temperature < UNPLAYABLE_THRESHOLDS.temperature.min) {
          weatherData.unplayableReason = `Too cold (${Math.round(weatherData.temperature)}°C < ${UNPLAYABLE_THRESHOLDS.temperature.min}°C threshold)`;
        } else if (weatherData.temperature > UNPLAYABLE_THRESHOLDS.temperature.max) {
          weatherData.unplayableReason = `Too hot (${Math.round(weatherData.temperature)}°C > ${UNPLAYABLE_THRESHOLDS.temperature.max}°C threshold)`;
        } else if (isWaterlogged) {
          weatherData.unplayableReason = `Waterlogged course (${groundQuality.effectiveRainfall}mm effective rainfall over 7 days)`;
        }
      }

      // Attach alerts from WeatherKit (location-level, same for all slots)
      weatherData.alerts = weatherKitData?.alerts || [];

      // Attach minute-level precipitation for rounds starting within 60 minutes
      const minutesUntilStart = (slotDate - Date.now()) / 60000;
      if (minutesUntilStart <= 60 && minutesUntilStart >= -10 && weatherKitData?.minutely?.length > 0) {
        weatherData.minuteForecast = weatherKitData.minutely;
      }

      return {
        ...slot,
        weather: weatherData,
        weatherScore: weatherData.golfScore?.score || 0,
        dataSource,
        accuracy: dataSource === 'hourly' ? 'high' : 'low'
      };
    });

  } catch (error) {
    console.error('Error in getWeatherForTimeSlots:', error);

    // Handle rate limit errors with specific message
    if (error instanceof RateLimitError) {
      return timeSlots.map(slot => ({
        ...slot,
        weather: null,
        weatherScore: 0,
        weatherError: error.message,
        rateLimitExceeded: true,
        rateLimitResetAt: error.resetAt,
        dataSource: 'rate-limited',
        accuracy: 'none'
      }));
    }

    return timeSlots.map(slot => ({
      ...slot,
      weather: null,
      weatherScore: 0,
      weatherError: 'Weather service temporarily unavailable',
      dataSource: 'error',
      accuracy: 'none'
    }));
  }
}

/**
 * Check if weather data is available for the given coordinates
 */
export function isWeatherAvailable(lat, lon) {
  return typeof lat === 'number' && typeof lon === 'number' &&
         lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

/**
 * Get weather suitability categories for filtering
 */
export function getWeatherCategories() {
  return [
    { value: 'ideal', label: 'Ideal', description: 'Perfect golf weather', minScore: 8.5 },
    { value: 'good', label: 'Good', description: 'Great for golf', minScore: 7.0 },
    { value: 'fair', label: 'Fair', description: 'Playable conditions', minScore: 4.0 },
    { value: 'poor', label: 'Poor', description: 'Challenging conditions', minScore: 0 }
  ];
}

// ============================================================
// GROUND QUALITY SCORING SYSTEM v2.0
// ============================================================
// Sophisticated playability-based ground quality scoring that
// converts rainfall history into a softness index using exponential
// decay, then applies a penalty curve distinguishing between firm,
// moderately soft, and severely waterlogged conditions.
//
// Key concepts:
// - R_eff (Effective Rain): Weighted sum with exponential decay α^i
// - S_soft (Softness Index): Normalised 0-1 saturation measure
// - G (Ground Playability): Penalty-adjusted 0-1 playability score
//
// See: plans/251211_ground-softness-v2.md for full specification
// ============================================================

// GROUND_CONFIG lives in scoringConfig.js (single source of truth for scoring tunables).

/**
 * Generate exponential decay weights for rainfall lookback
 * @param {number} alpha - Decay factor (0.6-0.75 typical)
 * @param {number} days - Number of days to generate weights for
 * @returns {Array<number>} - Array of weights [1.0, α, α², α³, ...]
 */
function generateDecayWeights(alpha, days) {
  return Array.from({ length: days }, (_, i) => Math.pow(alpha, i));
}

/**
 * Calculate ground quality score based on cumulative rainfall
 *
 * Uses the v2.0 playability-based scoring methodology:
 * 1. Calculate R_eff (effective rainfall) with exponential decay
 * 2. Convert to S_soft (softness index) using saturation curve
 * 3. Apply G (playability) penalty curve for final score
 *
 * @param {Array<number>} rainfallHistory - Daily rainfall totals [day0, day-1, ..., day-6] in mm
 * @param {number} temperature - Current/forecast temperature in °C
 * @param {number} humidity - Current/forecast humidity percentage
 * @param {number} windSpeedMph - Current/forecast wind speed in mph
 * @param {Array<number>} [et0History] - Daily reference ET (mm) [day0, ..., day-6] from Open-Meteo. day0 may be null/missing; estimated from current weather when so.
 * @returns {Object} - Ground quality result with score, category, and diagnostic fields
 */
export function calculateGroundQualityScore(rainfallHistory = [], temperature = 15, humidity = 50, windSpeedMph = 0, et0History = []) {
  const cfg = GROUND_CONFIG;

  // Handle empty or invalid input - return neutral estimate
  if (!Array.isArray(rainfallHistory) || rainfallHistory.length === 0) {
    return {
      score: 7.0,
      category: 'soft',
      effectiveRainfall: 0,
      grossRainfall: 0,
      totalEvap: 0,
      softnessIndex: null,
      playabilityScore: null,
      historyDays: 0,
      temperatureModifier: 'normal',
      isEstimate: true
    };
  }

  // === PHASE 1: Calculate R_eff (Effective Rainfall) ===
  //
  // Soil-moisture-balance model:
  //   for each day i in [0..6]:
  //     net_rain[i]      = max(0, rain[i] - ET0[i])   // physical evaporation
  //     intensity[i]     = 1 + max(0, (rain[i] - thresh) / div), capped
  //     weighted_rain[i] = net_rain[i] × intensity[i]
  //   R_eff = Σ weighted_rain[i] × α^i
  //
  // ET0 is the FAO Penman-Monteith reference evapotranspiration from
  // Open-Meteo. For the round day (i=0) when ET0 isn't in the cache, fall
  // back to the ET0_ESTIMATE heuristic using current weather conditions.
  const historyDays = Math.min(rainfallHistory.length, cfg.lookbackDays);
  const weights = generateDecayWeights(cfg.decayAlpha, historyDays);

  const todayET0Estimate = estimateET0(temperature, humidity, windSpeedMph);
  let R_eff = 0;
  let grossRainfall = 0;
  let totalEvap = 0;
  for (let i = 0; i < historyDays; i++) {
    const dailyRain = Math.max(0, rainfallHistory[i] || 0);
    // For past days: prefer cached Open-Meteo ET0. For day 0 (today/round day),
    // use the cached value if present, else estimate from current weather.
    const cachedET0 = et0History?.[i];
    const dailyET0 = (cachedET0 !== undefined && cachedET0 !== null && cachedET0 >= 0)
      ? cachedET0
      : (i === 0 ? todayET0Estimate : 0);

    const netRain = Math.max(0, dailyRain - dailyET0);
    const intensity = Math.min(
      INTENSITY_CONFIG.max,
      1 + Math.max(0, (dailyRain - INTENSITY_CONFIG.threshold) / INTENSITY_CONFIG.divisor)
    );

    grossRainfall += dailyRain;
    totalEvap += dailyET0;
    R_eff += netRain * intensity * weights[i];
  }

  // Ensure R_eff is non-negative
  R_eff = Math.max(0, R_eff);

  // === TEMPERATURE MODIFIER: Frozen Ground Override ===
  // Frozen ground is firm but may be icy - cap at the safety score
  if (temperature <= cfg.frozenTemp) {
    return {
      score: FROZEN_GROUND_SAFETY_SCORE,
      category: 'firm',
      effectiveRainfall: Math.round(R_eff * 10) / 10,
      grossRainfall: Math.round(grossRainfall * 10) / 10,
      totalEvap: Math.round(totalEvap * 10) / 10,
      softnessIndex: 0,
      playabilityScore: 0.8,  // Reflects the safety cap
      historyDays,
      temperatureModifier: 'frozen',
      frozenWarning: 'Course may be icy - exercise caution',
      isEstimate: historyDays < cfg.lookbackDays
    };
  }

  // === PHASE 2: Calculate S_soft (Softness Index) ===
  // Normalise R_eff against saturation threshold with cap at 1.5
  // S_soft = 1 - exp(-R_norm) gives smooth 0-1 curve
  const R_norm = Math.min(R_eff / cfg.saturationThreshold, cfg.saturationCap);
  let S_soft = 1 - Math.exp(-R_norm);

  // Clamp S_soft to [0, 1]
  S_soft = Math.max(0, Math.min(1, S_soft));

  // === PHASE 3: Calculate G (Ground Playability Score) ===
  // Two-phase penalty curve:
  // - Below firmThreshold: G = 1.0 (no penalty for firm ground)
  // - Above firmThreshold: Logistic penalty curve
  let G;
  if (S_soft <= cfg.firmThreshold) {
    // Firm ground - no penalty
    G = 1.0;
  } else {
    // Rescale S_soft to [0, 1] starting from threshold
    const x = (S_soft - cfg.firmThreshold) / (1 - cfg.firmThreshold);
    // Apply penalty curve: 1 / (1 + (x / steepness)²)
    // This creates a "knee" where penalties accelerate for very soft ground
    G = 1 / (1 + Math.pow(x / cfg.penaltyShape, 2));
  }

  // Clamp G to [0, 1]
  G = Math.max(0, Math.min(1, G));

  // Convert G to 0-10 scale for display
  let groundScore = G * 10;

  // The post-hoc drying multiplier (was DRYING_CONFIG, removed in this
  // commit) is no longer needed - evaporation is now subtracted from each
  // day's rainfall directly via ET0. A "modifier" label is still surfaced
  // for the UI, derived from whether net-of-evap rainfall exceeded or
  // undershot the gross.
  let temperatureModifier;
  const evapFraction = grossRainfall > 0 ? totalEvap / grossRainfall : 1;
  if (evapFraction >= 0.5 && grossRainfall >= 2) temperatureModifier = 'drying';
  else if (evapFraction <= 0.2 && grossRainfall >= 5) temperatureModifier = 'damp';
  else temperatureModifier = 'normal';

  // Final clamp and round
  groundScore = Math.max(0, Math.min(10, groundScore));
  groundScore = Math.round(groundScore * 10) / 10;

  // === CATEGORISE GROUND QUALITY ===
  let category;
  if (groundScore >= cfg.categories.firm) {
    category = 'firm';
  } else if (groundScore >= cfg.categories.soft) {
    category = 'soft';
  } else if (groundScore >= cfg.categories.wet) {
    category = 'wet';
  } else {
    category = 'waterlogged';
  }

  return {
    score: groundScore,
    category,
    effectiveRainfall: Math.round(R_eff * 10) / 10,
    grossRainfall: Math.round(grossRainfall * 10) / 10,
    totalEvap: Math.round(totalEvap * 10) / 10,
    softnessIndex: Math.round(S_soft * 100) / 100,  // 0-1 scale, 2 decimals
    playabilityScore: Math.round(G * 100) / 100,    // 0-1 scale, 2 decimals
    historyDays,
    temperatureModifier,
    isEstimate: historyDays < cfg.lookbackDays
  };
}

/**
 * Cheap heuristic estimate of FAO reference evapotranspiration when the
 * real Open-Meteo value isn't cached yet (e.g. for today's round-day
 * forecast). Calibrated against Leyland this week to be within ~1 mm/day
 * of the real ET0. Real ET0 is preferred whenever available.
 */
function estimateET0(temperature, humidity, windSpeedMph) {
  const c = ET0_ESTIMATE;
  const raw =
    c.base +
    c.tempCoef * Math.max(0, (temperature ?? 15) - c.tempZero) +
    c.windCoef * (windSpeedMph ?? 0) -
    c.humCoef  * Math.max(0, (humidity ?? 70) - c.humThresh);
  return Math.max(c.min, Math.min(c.max, raw));
}

/**
 * Extract daily rainfall totals from hourly forecast data
 * Aggregates hourly rain into daily buckets
 *
 * @param {Array} hourlyData - Array of hourly forecast objects from One Call API
 * @returns {Object} - Map of { 'YYYY-MM-DD': totalRainfall }
 */
export function extractDailyRainfallFromHourly(hourlyData) {
  const dailyRainfall = {};

  if (!Array.isArray(hourlyData)) return dailyRainfall;

  for (const hour of hourlyData) {
    const date = new Date(hour.dt * 1000);
    const dateKey = formatDateLocal(date);
    if (!dateKey) continue;

    const hourlyRain = hour.rain?.['1h'] || 0;
    dailyRainfall[dateKey] = (dailyRainfall[dateKey] || 0) + hourlyRain;
  }

  // Round to 1 decimal place
  for (const key in dailyRainfall) {
    dailyRainfall[key] = Math.round(dailyRainfall[key] * 10) / 10;
  }

  return dailyRainfall;
}

/**
 * Extract daily rainfall from One Call 3.0 daily data
 *
 * @param {Array} dailyData - Array of daily forecast objects from One Call API
 * @returns {Object} - Map of { 'YYYY-MM-DD': totalRainfall }
 */
export function extractDailyRainfallFromDaily(dailyData) {
  const dailyRainfall = {};

  if (!Array.isArray(dailyData)) return dailyRainfall;

  for (const day of dailyData) {
    const date = new Date(day.dt * 1000);
    const dateKey = formatDateLocal(date);
    if (!dateKey) continue;

    // One Call 3.0 daily format uses 'rain' directly (mm)
    // Include ALL days from the API, even if rain is 0 or undefined
    // This ensures complete rainfall history for ground quality calculations
    // getRainfallHistory() needs continuous date coverage to avoid gaps
    // Zero values won't overwrite day_summary data due to Math.max() merge logic in scoreTimeSlots
    const rain = day.rain || 0;
    dailyRainfall[dateKey] = rain;
  }

  return dailyRainfall;
}

/**
 * Build rainfall history for a specific round date
 * Looks back 7 days from the round date using available forecast data
 *
 * @param {Date|string} roundDate - The date of the round
 * @param {Object} rainfallBuffer - Map of { 'YYYY-MM-DD': rainfall } from forecast data
 * @returns {Array<number>} - [day0, day-1, day-2, ..., day-6] rainfall totals
 */
export function getRainfallHistory(roundDate, rainfallBuffer) {
  const history = [];
  const baseDate = new Date(roundDate);

  for (let i = 0; i < 7; i++) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() - i);
    const dateKey = formatDateLocal(date);

    // Use 0 if no data available (conservative assumption)
    history.push(rainfallBuffer[dateKey] || 0);
  }

  return history;
}

/**
 * Merge multiple rainfall data sources into a single buffer
 * Later sources override earlier ones for the same date
 *
 * @param {...Object} sources - Multiple rainfall buffer objects
 * @returns {Object} - Merged rainfall buffer
 */
export function mergeRainfallBuffers(...sources) {
  const merged = {};

  for (const source of sources) {
    if (source && typeof source === 'object') {
      Object.assign(merged, source);
    }
  }

  return merged;
}

// Re-export sun functions for convenience
export { getSunTimes, calculateDaylightScore } from './sunService.js';

// Export RateLimitError for components to detect rate limit issues
export { RateLimitError, formatResetTime };
